"use client";

// ═══════════════════════════════════════════════════════════════════════════════
// Bug-190 — useVersionCheck: detect version mismatch + force reload
// ═══════════════════════════════════════════════════════════════════════════════
//
// Bundle JS do Next.js fica em memoria do browser. Apos deploy novo, mesmo
// que API retorne fresh, JS antigo continua rodando. Hook checa periodicamente
// /api/version e compara com APP_VERSION local. Se diferente:
//   - mostra toast/banner avisando
//   - opcional: reload automatico apos N segundos
//
// Uso:
//   const { mismatch, serverVersion, forceReload } = useVersionCheck();
//
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useState, useCallback } from "react";
import { APP_VERSION } from "@/lib/version";

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutos

interface VersionInfo {
  version: string;
  timestamp: number;
}

export function useVersionCheck() {
  const [serverVersion, setServerVersion] = useState<string | null>(null);
  const [lastCheck, setLastCheck] = useState<number | null>(null);

  const checkVersion = useCallback(async () => {
    try {
      const res = await fetch(`/api/version?_t=${Date.now()}`, { cache: "no-store" });
      if (!res.ok) return;
      const data = (await res.json()) as VersionInfo;
      if (data?.version) {
        setServerVersion(data.version);
        setLastCheck(Date.now());
      }
    } catch {
      // silencioso — sem version check, segue
    }
  }, []);

  useEffect(() => {
    // check inicial logo apos mount
    checkVersion();
    // poll periodico
    const interval = setInterval(checkVersion, POLL_INTERVAL_MS);
    // re-check ao voltar de aba inativa
    const onVisibility = () => {
      if (document.visibilityState === "visible") checkVersion();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [checkVersion]);

  const mismatch = !!serverVersion && serverVersion !== APP_VERSION;

  const forceReload = useCallback(() => {
    // Limpa caches relevantes antes de reload
    try {
      // Limpa cache da API service worker se houver
      if ("caches" in window) {
        caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
      }
      // localStorage tem coisas (filtros, workspace ativo) — mantém
      // sessionStorage limpa
      sessionStorage.clear();
    } catch {
      /* noop */
    }
    // Hard reload — pede ao browser pra ignorar cache HTTP
    window.location.reload();
  }, []);

  return {
    mismatch,
    serverVersion,
    localVersion: APP_VERSION,
    lastCheck,
    forceReload,
  };
}
